import React, { useState, useRef, useEffect } from 'react';
import './ConsentModal.css';

const ConsentModal = ({ isOpen, onClose, onSubmit, userName }) => {
    const [signature, setSignature] = useState('');
    const [fullName, setFullName] = useState(userName || '');
    const [isDrawing, setIsDrawing] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const canvasRef = useRef(null);
    const [ctx, setCtx] = useState(null);

    useEffect(() => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            context.strokeStyle = '#000000';
            context.lineWidth = 2;
            context.lineCap = 'round';
            setCtx(context);
        }
    }, [isOpen]);

    const startDrawing = (e) => {
        if (!ctx) return;
        setIsDrawing(true);
        const rect = canvasRef.current.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const draw = (e) => {
        if (!isDrawing || !ctx) return;
        const rect = canvasRef.current.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (!ctx) return;
        setIsDrawing(false);
        ctx.closePath();
        // Save signature as base64
        const signatureData = canvasRef.current.toDataURL();
        setSignature(signatureData);
    };

    const clearSignature = () => {
        if (!ctx) return;
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setSignature('');
    };

    const handleSubmit = () => {
        if (!fullName.trim()) {
            alert('Please enter your full name');
            return;
        }
        if (!signature) {
            alert('Please provide your signature');
            return;
        }
        if (!agreedToTerms) {
            alert('Please agree to the terms and conditions');
            return;
        }

        onSubmit({
            fullName,
            signature,
            consentText: getConsentText()
        });
    };

    const getConsentText = () => {
        return `INFORMED CONSENT FOR ACCESS TO DIGITAL HEALTH JOURNAL

By signing this form, I, ${fullName}, agree to give Dr. Within by True Whealth permission to access and review all entries from my digital health journal for the purpose of supporting me during one-on-one discussions with a naturopathic practitioner. These discussions are intended to offer wellness guidance, education, and support based on the information I have shared.

By providing consent, I acknowledge and agree that:

VOLUNTARY ACCESS
• I am choosing to allow Dr Within and its designated naturopathic practitioner to view my journal entries.
• I may withdraw access of my journal entries at any time.

PURPOSE OF ACCESS
• My entries will be reviewed solely to help guide a personalized wellness one-on-one discussion.
• These discussions are not medical consultations, do not diagnose conditions, and do not replace medical, psychological, or other professional healthcare services.

PRIVACY & CONFIDENTIALITY
• My information will be handled confidentially and stored securely within the system.
• Only the naturopathic practitioner involved in my one-on-one discussion will have access to my shared entries.
• My information will not be shared with third parties unless required by law or with my explicit permission.

YOUR RIGHTS
• I may revoke this consent at any time by contacting info@drwithin.shop
• I may request to see what information has been accessed or stored.
• I may decline to share any entry without affecting my ability to use the platform.

LIMITS OF SERVICE
• The team does not provide medical treatment, diagnosis, or emergency support.
• If I have urgent health concerns, I agree to seek care from a licensed healthcare professional or emergency services.

CONSENT
By signing below, I confirm that:
• I have read and understood this informed consent.
• I voluntarily agree to allow Dr. Within by True Whealth to access my digital journal entries for the purposes described above.
• I understand that I may withdraw consent at any time.

Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
Electronic Signature: [Provided]
Full Name: ${fullName}`;
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="consent-modal" onClick={(e) => e.stopPropagation()}>
                <div className="consent-modal-header">
                    <h2>Consent to Share Health Journal</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="consent-modal-body">
                    <div className="consent-content">
                        <h3>Informed Consent for Access to Digital Health Journal</h3>

                        <div className="consent-section">
                            <p>
                                By signing this form, you agree to give Dr. Within by True Whealth permission to access and
                                review all entries from your digital health journal for the purpose of supporting you during our
                                one-on-one discussions with a naturopathic practitioner. These discussions are intended to offer
                                wellness guidance, education, and support based on the information you have shared.
                            </p>
                            <p>
                                By providing consent, you acknowledge and agree that:
                            </p>
                        </div>

                        <div className="consent-section">
                            <h4>Voluntary Access</h4>
                            <ul>
                                <li>You are choosing to allow Dr Within and its designated naturopathic practitioner to view your journal entries.</li>
                                <li>You may withdraw access of your journal entries at any time.</li>
                            </ul>
                        </div>

                        <div className="consent-section">
                            <h4>Purpose of Access</h4>
                            <ul>
                                <li>Your entries will be reviewed solely to help guide a personalized wellness one-on-one discussion.</li>
                                <li>These discussions are not medical consultations, do not diagnose conditions, and do not replace medical, psychological, or other professional healthcare services.</li>
                            </ul>
                        </div>

                        <div className="consent-section">
                            <h4>Privacy & Confidentiality</h4>
                            <ul>
                                <li>Your information will be handled confidentially and stored securely within our system.</li>
                                <li>Only the naturopathic practitioner involved in your one-on-one discussion will have access to your shared entries.</li>
                                <li>Your information will not be shared with third parties unless required by law or with your explicit permission.</li>
                            </ul>
                        </div>

                        <div className="consent-section">
                            <h4>Your Rights</h4>
                            <ul>
                                <li>You may revoke this consent at any time by contacting us at <strong>info@drwithin.shop</strong></li>
                                <li>You may request to see what information has been accessed or stored.</li>
                                <li>You may decline to share any entry without affecting your ability to use our platform.</li>
                            </ul>
                        </div>

                        <div className="consent-section">
                            <h4>Limits of Service</h4>
                            <ul>
                                <li>Our team does not provide medical treatment, diagnosis, or emergency support.</li>
                                <li>If you have urgent health concerns, you agree to seek care from a licensed healthcare professional or emergency services.</li>
                            </ul>
                        </div>

                        <div className="consent-section">
                            <h4>Consent</h4>
                            <p>By signing below, you confirm that:</p>
                            <ul>
                                <li>You have read and understood this informed consent.</li>
                                <li>You voluntarily agree to allow Dr. Within by True Whealth to access your digital journal entries for the purposes described above.</li>
                                <li>You understand that you may withdraw consent at any time.</li>
                            </ul>
                        </div>
                    </div>

                    <div className="consent-form">
                        <div className="form-group">
                            <label>Full Name *</label>
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="Enter your full name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Electronic Signature *</label>
                            <p className="signature-instruction">
                                Please sign below using your mouse or touch screen
                            </p>
                            <div className="signature-container">
                                <canvas
                                    ref={canvasRef}
                                    width={500}
                                    height={150}
                                    className="signature-canvas"
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={(e) => {
                                        e.preventDefault();
                                        startDrawing(e.touches[0]);
                                    }}
                                    onTouchMove={(e) => {
                                        e.preventDefault();
                                        draw(e.touches[0]);
                                    }}
                                    onTouchEnd={stopDrawing}
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary clear-signature-btn"
                                    onClick={clearSignature}
                                >
                                    Clear Signature
                                </button>
                            </div>
                        </div>

                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                />
                                <span>
                                    I have read and understood this informed consent, and I voluntarily agree
                                    to allow Dr. Within by True Whealth to access my digital journal entries
                                    for the purposes described above. I understand that I may withdraw consent
                                    at any time.
                                </span>
                            </label>
                        </div>

                        <div className="consent-date">
                            <strong>Date:</strong> {new Date().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </div>
                    </div>
                </div>

                <div className="consent-modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={!fullName.trim() || !signature || !agreedToTerms}
                    >
                        Submit Consent
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConsentModal;
